import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ArrowLeft, ShoppingCart, Tag } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useCart } from "../context/CartContext";
import { formatBRL } from "../lib/format";
import OrderSummary from "../components/OrderSummary";

const Cart = () => {
  const { items, updateQty, removeFromCart, applyCoupon, coupon } = useCart();
  const [code, setCode] = useState("");
  const navigate = useNavigate();

  const apply = () => {
    if (applyCoupon(code)) toast.success("Cupom aplicado com sucesso!");
    else toast.error("Cupom inválido. Tente SULBEL10");
  };

  if (items.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <ShoppingCart className="h-14 w-14 text-slate-300 mx-auto" />
        <h1 className="mt-4 text-2xl font-bold text-slate-900">Meu carrinho</h1>
        <p className="mt-2 text-slate-500">Seu carrinho está vazio.</p>
        <Button asChild className="mt-6 bg-brand hover:bg-brand-hover"><Link to="/loja">Continuar comprando</Link></Button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-6">Meu carrinho</h1>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {items.map((i) => (
            <div
              key={i.product.id}
              data-testid={`cart-item-${i.product.id}`}
              className="flex flex-col sm:flex-row gap-4 bg-white border border-slate-200 rounded-lg p-4"
            >
              <Link to={`/produto/${i.product.slug}`} className="shrink-0">
                <img src={i.product.image} alt={i.product.name} className="h-24 w-24 rounded-md object-cover bg-slate-50 border border-slate-200" />
              </Link>
              <div className="flex-1 min-w-0">
                <span className="text-xs uppercase tracking-wide text-slate-400">{i.product.category}</span>
                <Link to={`/produto/${i.product.slug}`}><h3 className="font-semibold text-slate-900 hover:text-brand transition-colors">{i.product.name}</h3></Link>
                <p className="text-sm text-slate-500 mt-0.5">{formatBRL(i.product.price)} / un.</p>

                <div className="mt-3 flex items-center justify-between gap-3">
                  <div className="flex items-center border border-slate-300 rounded-md">
                    <button onClick={() => updateQty(i.product.id, i.qty - 1)} data-testid={`cart-decrease-${i.product.id}`} className="h-9 w-9 flex items-center justify-center text-slate-600 hover:bg-slate-50"><Minus className="h-4 w-4" /></button>
                    <span className="w-9 text-center text-sm font-semibold" data-testid={`cart-qty-${i.product.id}`}>{i.qty}</span>
                    <button onClick={() => updateQty(i.product.id, i.qty + 1)} data-testid={`cart-increase-${i.product.id}`} className="h-9 w-9 flex items-center justify-center text-slate-600 hover:bg-slate-50"><Plus className="h-4 w-4" /></button>
                  </div>
                  <button
                    onClick={() => { removeFromCart(i.product.id); toast("Item removido do carrinho"); }}
                    data-testid={`cart-remove-${i.product.id}`}
                    className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-red-600 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" /> Remover
                  </button>
                </div>
              </div>
              <div className="sm:text-right shrink-0 sm:w-28">
                <p className="text-xs text-slate-400">Subtotal</p>
                <p className="text-lg font-extrabold text-slate-900">{formatBRL(i.product.price * i.qty)}</p>
              </div>
            </div>
          ))}

          <Link to="/loja" className="inline-flex items-center gap-2 text-sm font-semibold text-brand hover:text-brand-hover transition-colors">
            <ArrowLeft className="h-4 w-4" /> Continuar comprando
          </Link>
        </div>

        {/* Summary */}
        <div className="lg:col-span-1">
          <div className="sticky top-24 space-y-4">
            <div className="bg-white border border-slate-200 rounded-lg p-5">
              <label className="text-sm font-semibold text-slate-800 flex items-center gap-1.5 mb-2"><Tag className="h-4 w-4 text-brand" /> Cupom de desconto</label>
              <div className="flex gap-2">
                <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Digite seu cupom" className="h-11 border-slate-300" data-testid="coupon-input" />
                <Button onClick={apply} variant="outline" className="h-11 border-slate-300" data-testid="apply-coupon-button">Aplicar</Button>
              </div>
              {coupon && <p className="mt-2 text-xs text-emerald-600 font-medium">Cupom {coupon.code} ({coupon.percent}%) aplicado.</p>}
            </div>

            <OrderSummary
              footer={
                <Button
                  onClick={() => navigate("/checkout")}
                  data-testid="cart-checkout-button"
                  className="mt-5 w-full h-12 bg-brand hover:bg-brand-hover text-white font-semibold rounded-md active:scale-[0.98] transition-all"
                >
                  Continuar para o checkout
                </Button>
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
